import type { Locale } from "@sss/shared";
import type { Messages } from "./messages";
import type { PluralForms } from "./types";

/**
 * Replaces `{name}` placeholders. Values are stringified as-is: a template
 * with no placeholders and no values is returned untouched, which is the
 * common case.
 */
export function interpolate(template: string, values?: Record<string, string | number>): string {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  );
}

const pluralRules = new Map<Locale, Intl.PluralRules>();

function rulesFor(locale: Locale): Intl.PluralRules {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules;
}

/**
 * Picks the form matching `count` by the runtime's own ICU rules, then
 * interpolates — with `count` always available as `{count}`, so a form
 * never has to be passed the number it is about.
 */
export function selectPlural(
  locale: Locale,
  forms: PluralForms,
  count: number,
  values?: Record<string, string | number>,
): string {
  const form = forms[rulesFor(locale).select(count)] ?? forms.other;
  return interpolate(form, { count, ...values });
}

const numberFormats = new Map<string, Intl.NumberFormat>();

function decimalFormat(locale: Locale, digits: number): Intl.NumberFormat {
  const cacheKey = `${locale}:${digits}`;
  let format = numberFormats.get(cacheKey);
  if (!format) {
    format = new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    numberFormats.set(cacheKey, format);
  }
  return format;
}

/**
 * A byte count in the largest unit that keeps it above 1, formatted for the
 * locale — the separator is a comma in French and German ("1,5 Mo"), a dot
 * in English. The unit itself comes from the message file, because it is
 * not universal either: French writes octets, not bytes.
 */
export function formatBytes(bytes: number, locale: Locale, units: Messages["bytes"]): string {
  if (bytes < 1024) {
    return interpolate(units.b, { size: decimalFormat(locale, 0).format(bytes) });
  }

  const scaled = [units.kb, units.mb, units.gb];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < scaled.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return interpolate(scaled[unitIndex], { size: decimalFormat(locale, 1).format(value) });
}

const timeFormats = new Map<Locale, Intl.DateTimeFormat>();

/**
 * The wall-clock time a retrieval was verified, in UTC.
 *
 * UTC rather than the reader's zone on purpose: this line is a receipt that
 * may be quoted back to whoever sent the secret, or matched against the
 * API's audit log, and both of those are in UTC. The zone is named in the
 * output so it can't be mistaken for local time.
 */
export function formatUtcTime(timestamp: number, locale: Locale): string {
  let format = timeFormats.get(locale);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone: "UTC",
      timeZoneName: "short",
    });
    timeFormats.set(locale, format);
  }
  return format.format(new Date(timestamp));
}
